// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	isVoiceRoomStageBlock,
	isVoiceRoomStageLine,
	VOICE_ROOM_STAGE_LIMITS as L,
	VOICE_ROOM_STAGE_BOARD_STYLES,
	VOICE_ROOM_STAGE_HOST_EMOTES,
} from '@fluxer/constants/src/VoiceRoomStage';
import {ColorType, SnowflakeStringType} from '@fluxer/schema/src/primitives/SchemaPrimitives';
import {schemaMetadata} from '@fluxer/schema/src/SchemaMetadata';
import {z} from 'zod';

const line = (min: number, max: number) =>
	z.string().trim().min(min).max(max).refine(isVoiceRoomStageLine, 'Plain text on one line only');
const block = (max: number) =>
	z.string().trim().max(max).refine(isVoiceRoomStageBlock, `Plain text, at most ${L.textLinesMax} lines`);
const ScoreValue = z.number().int().min(-L.scoreValueMax).max(L.scoreValueMax);
const CharacterKey = z.string().regex(/^[a-z0-9-]{1,32}$/);
const Epoch = z.number().int().min(0);

const BoardStyle = z.enum(VOICE_ROOM_STAGE_BOARD_STYLES).describe('Look of the board');

const StageBoardRequest = z
	.object({
		type: z.literal('board'),
		style: BoardStyle.optional().describe('Look of the board; defaults to neon'),
		title: line(0, L.titleMax).nullish().describe('Heading of the board'),
		text: block(L.textMax).nullish().describe('Body text of the board, line breaks kept'),
		accent: ColorType.optional().describe('Accent colour as an RGB integer'),
		scores: z
			.array(
				z.object({
					name: line(1, L.scoreNameMax).describe('Name shown on the scoreboard row'),
					value: ScoreValue.describe('Score of the row'),
					user_id: SnowflakeStringType.optional().describe('User the row is about; their avatar shows beside it'),
				}),
			)
			.max(L.scoresMax)
			.optional()
			.describe('Scoreboard rows in the order shown'),
		message_id: SnowflakeStringType.optional().describe(
			"The bot's own message in this channel whose buttons the board shows",
		),
	})
	.refine((board) => Boolean(board.title) || Boolean(board.text) || (board.scores?.length ?? 0) > 0, {
		message: 'A board needs a title, a text or scores',
	});

const StageVideoRequest = z.object({
	type: z.literal('video'),
	url: z.string().min(1).max(2048).describe('YouTube video link to play on the room TV'),
	position_ms: z
		.number()
		.int()
		.min(0)
		.max(86_400_000)
		.optional()
		.describe('Playback position in milliseconds; defaults to 0'),
	paused: z.boolean().optional().describe('Whether the video stands paused at position_ms; defaults to false'),
});

export const PutVoiceRoomStageRequest = z.object({
	tv: z
		.discriminatedUnion('type', [StageBoardRequest, StageVideoRequest])
		.nullable()
		.optional()
		.describe('What the room TV shows; null or absent: nothing from the stage'),
	host: z
		.object({
			character: CharacterKey.optional().describe('Room character preset of the host; defaults to the default one'),
			shirt: ColorType.optional().describe('Shirt colour of the host as an RGB integer'),
		})
		// {} is the default host, not null: the API turns empty request objects into null otherwise.
		.register(schemaMetadata, {preserveEmptyValues: true})
		.nullable()
		.optional()
		.describe('The host character standing in the room; null or absent: none'),
	ttl_seconds: z
		.number()
		.int()
		.min(L.ttlSecondsMin)
		.max(L.ttlSecondsMax)
		.optional()
		.describe(`Seconds the stage shows before it lapses; defaults to ${L.ttlSecondsDefault}`),
});

export type PutVoiceRoomStageRequest = z.infer<typeof PutVoiceRoomStageRequest>;

const StageBoard = z.object({
	type: z.literal('board'),
	style: BoardStyle,
	title: line(0, L.titleMax),
	text: block(L.textMax),
	accent: ColorType.nullable(),
	scores: z
		.array(
			z.object({
				name: line(1, L.scoreNameMax),
				value: ScoreValue,
				user_id: SnowflakeStringType.nullable(),
			}),
		)
		.max(L.scoresMax),
	buttons: z
		.object({
			message_id: SnowflakeStringType,
			items: z
				.array(
					z.object({
						custom_id: z.string().min(1).max(L.customIdMax),
						label: line(0, L.buttonLabelMax),
						style: z.number().int().min(1).max(4).describe('Button style: 1 primary, 2 secondary, 3 success, 4 danger'),
						disabled: z.boolean(),
					}),
				)
				.min(1)
				.max(L.buttonsMax),
		})
		.nullable()
		.describe("Buttons copied from the bot's message; a click goes to that message's component route"),
});

const StageVideo = z.object({
	type: z.literal('video'),
	video_id: z.string().regex(/^[A-Za-z0-9_-]{11}$/),
	started_at: Epoch.describe('API time in milliseconds when the video was at 0'),
	paused_at: Epoch.nullable().describe('API time in milliseconds the video stands paused at; null while playing'),
});

export const VoiceRoomStage = z.object({
	v: z.literal(1),
	bot: z
		.object({
			id: SnowflakeStringType,
			name: line(1, L.botNameMax),
			avatar: z.string().max(64).nullable(),
		})
		.describe('The bot running the stage, from its own account'),
	written_at: Epoch.describe('API time in milliseconds the stage was written'),
	expires_at: Epoch.describe('API time in milliseconds after which clients no longer show the stage'),
	tv: z.discriminatedUnion('type', [StageBoard, StageVideo]).nullable(),
	host: z.object({character: CharacterKey, shirt: ColorType}).nullable(),
});

export type VoiceRoomStage = z.infer<typeof VoiceRoomStage>;

export const VoiceRoomStageResponse = z.object({
	stage: VoiceRoomStage.nullable().describe('The stage the room shows now; null when there is none'),
});

export type VoiceRoomStageResponse = z.infer<typeof VoiceRoomStageResponse>;

export const PostVoiceRoomStageEffectRequest = z.discriminatedUnion('type', [
	z.object({type: z.literal('confetti')}),
	z.object({type: z.literal('spotlight'), user_id: SnowflakeStringType.describe('User the spotlight falls on')}),
	z
		.object({
			type: z.literal('host_emote'),
			emote: z.enum(VOICE_ROOM_STAGE_HOST_EMOTES).describe('Movement of the host character'),
			emoji: z.string().min(1).max(L.emojiMax).optional().describe('Unicode emoji for the emoji emote'),
		})
		.refine((effect) => (effect.emote === 'emoji') === (effect.emoji !== undefined), {
			message: 'The emoji emote needs an emoji, and only it takes one',
		}),
]);

export type VoiceRoomStageEffect = z.infer<typeof PostVoiceRoomStageEffectRequest>;

export function encodeVoiceRoomStageMetadata(stage: VoiceRoomStage): string {
	return JSON.stringify({stage});
}

// The room metadata as a stage: anything else (empty, another writer's, broken, over the budget) is no stage.
export function decodeVoiceRoomStageMetadata(metadata: string | undefined): VoiceRoomStage | null {
	if (!metadata || metadata.length > L.metadataBytesMax) return null;
	try {
		const parsed = VoiceRoomStage.safeParse((JSON.parse(metadata) as {stage?: unknown} | null)?.stage);
		return parsed.success ? parsed.data : null;
	} catch {
		return null;
	}
}

export function decodeVoiceRoomStageEffect(payload: Uint8Array): VoiceRoomStageEffect | null {
	if (payload.byteLength > L.effectBytesMax) return null;
	try {
		const parsed = PostVoiceRoomStageEffectRequest.safeParse(JSON.parse(new TextDecoder().decode(payload)));
		return parsed.success ? parsed.data : null;
	} catch {
		return null;
	}
}
